import uniqid from 'uniqid';
import states from '@utils/states';
import AjaxLoader from '../ajax-loader/ajax-loader';
import AddressValidation from '../address-validation/address-validation';
import addressBookStore from '@stores/address-book/address-book-store';
import template from './templates/address-book-form';
import { context as defaultContext } from './address-book-defaults';

const CLASS_NAME_ERROR = 'error';
const CLASS_NAME_LOADING = '-loading';

const EVENT_KEY = '_addressBookForm_';
const EVENT_INPUT = `input.${EVENT_KEY}`;

const DEFAULT_LOADER_OPTIONS = {
    replaceOriginEl: false,
    size: 'lg'
};

const DEFAULT_CONTEXT = {
    cancel: defaultContext.Cancel,
    heading: defaultContext.AddShippingAddress,
    loadingIcon: null,
    requiredToken: defaultContext.RequiredToken,
    save: defaultContext.SaveAddress,
    loadingSrText: defaultContext.LoadingSrText,
    validation: defaultContext.RequiredMessage
};

export const DEFAULT_OPTIONS = {
    addressId: null,
    onAfterSave: null,
    onCancel: null,
    replaceOriginEl: false,
    schema: {
        primaryAddress: {
            required: false,
            name: 'IsPrimary',
            label: 'Set as primary address',
            value: false
        },
        firstName: {
            required: true,
            name: 'FirstName',
            label: 'First Name',
            value: ''
        },
        lastName: {
            required: true,
            name: 'LastName',
            label: 'Last Name',
            value: ''
        },
        address: {
            required: true,
            name: 'Address1',
            label: 'Street Address',
            value: ''
        },
        address2: {
            required: false,
            name: 'Address2',
            label: 'Address Line 2',
            value: ''
        },
        city: {
            required: true,
            name: 'City',
            label: 'City',
            value: ''
        },
        state: {
            required: true,
            name: 'State',
            label: 'State',
            value: ''
        },
        zip: {
            required: true,
            name: 'Zip',
            label: 'Zip',
            value: ''
        },
        phone: {
            required: false,
            name: 'Phone',
            label: 'Phone',
            value: ''
        }
    }
};

export default class AddressBookForm {
    constructor($originEl, options, context) {
        context = { ...DEFAULT_CONTEXT, ...context };
        options = { ...DEFAULT_OPTIONS, ...options };

        const $parsed = $($.parseHTML(template));
        this.$el = $parsed;
        this.$originEl = $originEl;

        // non-field labels
        this.$headingLabel = this.$el.find('[data-label="heading"]');
        this.$cancelLabel = this.$el.find('[data-label="cancel"]');
        this.$saveLabel = this.$el.find('[data-label="save"]');

        // field labels
        this.$primaryAddressLabel = this.$el.find('[data-label="primaryAddress"]');
        this.$firstNameLabel = this.$el.find('[data-label="firstName"]');
        this.$lastNameLabel = this.$el.find('[data-label="lastName"]');
        this.$addressLabel = this.$el.find('[data-label="address"]');
        this.$address2Label = this.$el.find('[data-label="address2"]');
        this.$cityLabel = this.$el.find('[data-label="city"]');
        this.$stateLabel = this.$el.find('[data-label="state"]');
        this.$zipLabel = this.$el.find('[data-label="zip"]');
        this.$phoneLabel = this.$el.find('[data-label="phone"]');

        // field error labels
        this.$primaryAddressErrorLabel = this.$el.find('[data-label="primaryAddressError"]');
        this.$firstNameErrorLabel = this.$el.find('[data-label="firstNameError"]');
        this.$lastNameErrorLabel = this.$el.find('[data-label="lastNameError"]');
        this.$addressErrorLabel = this.$el.find('[data-label="addressError"]');
        this.$address2ErrorLabel = this.$el.find('[data-label="address2Error"]');
        this.$cityErrorLabel = this.$el.find('[data-label="cityError"]');
        this.$stateErrorLabel = this.$el.find('[data-label="stateError"]');
        this.$zipErrorLabel = this.$el.find('[data-label="zipError"]');
        this.$phoneErrorLabel = this.$el.find('[data-label="phoneError"]');

        // field controls (input)
        this.$primaryAddress = this.$el.find('[data-control="primaryAddress"]');
        this.$firstNameCtrl = this.$el.find('[data-control="firstName"]');
        this.$lastNameCtrl = this.$el.find('[data-control="lastName"]');
        this.$addressCtrl = this.$el.find('[data-control="address"]');
        this.$address2Ctrl = this.$el.find('[data-control="address2"]');
        this.$cityCtrl = this.$el.find('[data-control="city"]');
        this.$stateCtrl = this.$el.find('[data-control="state"]');
        this.$zipCtrl = this.$el.find('[data-control="zip"]');
        this.$phoneCtrl = this.$el.find('[data-control="phone"]');

        this.$cancelCtrl = this.$el.find('[data-control="cancel"]');
        
        this.$error = this.$el.find('[data-region="error"]');
        this.$form = this.$el.find('[data-region="form"]');
        this.$loader = this.$el.find('[data-region="loader"]');

        this.replaceValidationToken = (str) => context.validation.replace('{0}', str);
        
        this.loader = null;
        this.unwatchStore = null;

        this.addressId = options.addressId;
        this.onAfterSave = options.onAfterSave;
        this.onCancel = options.onCancel;
        this.replaceOriginEl = options.replaceOriginEl;

        this.schema = Object.fromEntries(
            Object.entries(options.schema).map(([ key, value]) => {
                const id = uniqid.time(`${key}_`);
                const errorId = `${id}__error`;
                const error = value.error && value.required ? value.error : (context.validation?.replace('{0}', value.label) || 'This field is required');
                const label = !value.required ? value.label : value.label + context.requiredToken;

                return [ key, { ...value, error, errorId, id, label } ];
            })
        );

        this.context = context;

        this.init();
    }

    async #save() {
        this.$error.hide().empty();

        const formData = new FormData(this.$form.get(0));
        const addressParty = Object.fromEntries(formData.entries());
        const { success, data, error, showSuggestionPrompt } = await addressBookStore.actions.addOrEditAddress({
            addressId: this.addressId,
            addressParty
        });

        if(success) {
            let addressConfirmed = !showSuggestionPrompt;

            if(showSuggestionPrompt) {
                addressConfirmed = await this.#handleSuggestions(data.UpsAddressValidationStatus, formData, addressParty, data.SuggestedAddresses);
            }

            if(addressConfirmed) {
                this.onAfterSave?.();
            }
        } else {
            this.$error.html(error).show();
        }
    }

    async #handleSuggestions(upsStatus, formData, originalAddressParty, suggestedAddresses = []) {
        let isConfirmed = false;

        const currentAddress = {
            address: formData.get(this.schema.address.name),
            address2: formData.get(this.schema.address2.name),
            city: formData.get(this.schema.city.name),
            stateCode: formData.get(this.schema.state.name),
            zip: formData.get(this.schema.zip.name)
        };

        const suggestions = suggestedAddresses?.map((address, index) => ({
            addressId: `${index}`,
            address: address.Address1,
            address2: address.Address2,
            city: address.City,
            stateCode: address.StateCode,
            zip: address.ZipPostalCode
        }));

        const onSubmitHandler = async (isOriginal, value) => {
            let addressParty = originalAddressParty;

            if(!isOriginal) {
                const index = suggestions.findIndex(({ addressId }) => addressId === value);
                if(index > -1) {
                    addressParty = {
                        ...addressParty,
                        ...suggestedAddresses[index]
                    };
                }
            }

            const response = await addressBookStore.actions.addOrEditAddress({
                addressId: this.addressId,
                addressParty,
                isUserConfirmed: true
            });

            if(response.success) {
                isConfirmed = true;
            }

            return response;
        };

        const addressValidation = new AddressValidation(
            upsStatus,
            currentAddress,
            suggestions,
            this.context,
            {},
            onSubmitHandler
        );

        await addressValidation.prompt();
        addressValidation.destroy();
        return isConfirmed;
    }

    destroy() {
        this.unwatchStore?.();
        this.$el.remove();
    }

    init() {
        this.setupIds();
        this.bindElementsToSchema();
        this.renderLabels();
        this.renderStates();
        this.renderAttributes();
        this.setupLoader();
        this.setupListeners();
    }

    bindElementsToSchema() {
        this.schema = Object.fromEntries(
            Object.entries(this.schema).map(([ key, value ]) => {
                const $error = this.$el.find(`#${value.errorId}`);
                const $input = this.$el.find(`#${value.id}`);
                const $container = $input.closest('[data-region="field"]');
                const $label = this.$el.find(`[for=${value.id}]`);
                return [ key, { ...value, $container, $error, $input, $label } ];
            })
        );
    }

    onCancelClick() {
        this.onCancel?.();
    }

    onLoadingChange(loadingForm) {
        this.loader.active = loadingForm;

        if(loadingForm) {
            this.$loader.addClass(CLASS_NAME_LOADING);
        } else {
            this.$loader.removeClass(CLASS_NAME_LOADING);
        }
    }

    onInputChange(evt) {
        $(evt.currentTarget).data('changed', true);
    }

    onValidatedInput($container, $input, errorId) {
        if($input.is(':valid')) {
            $container.removeClass(CLASS_NAME_ERROR);
            $input
                .removeClass(CLASS_NAME_ERROR)
                .attr({ 'aria-describedby ': null, 'aria-invalid': false });
        } else {
            $container.addClass(CLASS_NAME_ERROR);
            $input
                .addClass(CLASS_NAME_ERROR)
                .attr({ 'aria-describedby': errorId, 'aria-invalid': true });
        }
    }

    onSubmit(evt) {
        evt.preventDefault();
        evt.stopPropagation();

        if(!this.$form.get(0).checkValidity()) {
            this.validate();
        } else {
            this.#save();
        }
    }

    render() {
        if(this.$originEl) {
            if(this.replaceOriginEl) {
                this.$originEl.replaceWith(this.$el);
            } else {
                this.$originEl.html(this.$el);
            }
        }

        return this.$el;
    }

    renderAttributes() {
        Object.values(this.schema).forEach((field) => {
            if(field.$input.is('[type=text],select')) {
                field.$input
                    .attr({
                        'placeholder': field.label,
                        'name': field.name,
                        'aria-required': field.required
                    })
                    .prop('required', field.required)
                    .val(field.value);
            } else if(field.$input.is('[type=checkbox]')) {
                field.$input
                    .attr({
                        'name': field.name,
                        'aria-required': field.required
                    })
                    .prop({
                        'checked': field.value,
                        'required': field.required
                    });
            }
        });
    }

    renderLabels() {
        this.$headingLabel.html(this.context.heading);
        this.$cancelLabel.html(this.context.cancel);
        this.$saveLabel.html(this.context.save);

        Object.values(this.schema).forEach((field) => {
            field.$label.html(field.label);
            field.$error.html(field.error);
        });
    }

    renderStates() {
        const fragment = document.createDocumentFragment();

        states.forEach((state) => {
            const option = document.createElement('option');
            option.value = state.value;
            option.innerHTML = state.text;
            fragment.appendChild(option);
        });

        this.$stateCtrl.append(fragment);
    }

    setupIds() {
        this.$primaryAddress.attr('id', this.schema.primaryAddress.id);
        this.$firstNameCtrl.attr('id', this.schema.firstName.id);
        this.$lastNameCtrl.attr('id', this.schema.lastName.id);
        this.$addressCtrl.attr('id', this.schema.address.id);
        this.$address2Ctrl.attr('id', this.schema.address2.id);
        this.$cityCtrl.attr('id', this.schema.city.id);
        this.$stateCtrl.attr('id', this.schema.state.id);
        this.$zipCtrl.attr('id', this.schema.zip.id);
        this.$phoneCtrl.attr('id', this.schema.phone.id);

        this.$primaryAddressLabel.attr('for', this.schema.primaryAddress.id);
        this.$firstNameLabel.attr('for', this.schema.firstName.id);
        this.$lastNameLabel.attr('for', this.schema.lastName.id);
        this.$addressLabel.attr('for', this.schema.address.id);
        this.$address2Label.attr('for', this.schema.address2.id);
        this.$cityLabel.attr('for', this.schema.city.id);
        this.$stateLabel.attr('for', this.schema.state.id);
        this.$zipLabel.attr('for', this.schema.zip.id);
        this.$phoneLabel.attr('for', this.schema.phone.id);

        this.$primaryAddressErrorLabel.attr('id', this.schema.primaryAddress.errorId);
        this.$firstNameErrorLabel.attr('id', this.schema.firstName.errorId);
        this.$lastNameErrorLabel.attr('id', this.schema.lastName.errorId);
        this.$addressErrorLabel.attr('id', this.schema.address.errorId);
        this.$address2ErrorLabel.attr('id', this.schema.address2.errorId);
        this.$cityErrorLabel.attr('id', this.schema.city.errorId);
        this.$stateErrorLabel.attr('id', this.schema.state.errorId);
        this.$zipErrorLabel.attr('id', this.schema.zip.errorId);
        this.$phoneErrorLabel.attr('id', this.schema.phone.errorId);
    }

    setupListeners() {
        this.$cancelCtrl.on('click', () => this.onCancelClick());
        this.$form.on('submit', (evt) => this.onSubmit(evt));
        this.$form.on('change', (evt) => this.onInputChange(evt));

        this.$form.find('input[type="text"], select[data-control="state"]').each((idx,el) => {
            $(el).on('change keyup', () => {
                let formSubmitButton = $(this.$form).find(':submit');
                $(formSubmitButton).removeAttr('disabled');
            });
        });

        this.unwatchStore = addressBookStore.watch('loadingForm', (...[,,loadingForm]) => this.onLoadingChange(loadingForm));
    }

    setupLoader() {
        this.loader = new AjaxLoader(this.$loader, {
            ...DEFAULT_LOADER_OPTIONS,
            customIcon: this.context.loadingIcon,
            srText: this.context.loadingSrText
        });

        this.loader.render();
    }

    focus() {
        this.$headingLabel
            .attr('tabindex', '0')
            .one('blur', () => this.$headingLabel.removeAttr('tabindex'))
            .get(0)
            .focus();
    }

    validate() {
        Object.values(this.schema).forEach((field) => {
            if(field.required && !field.$input.val()) {
                field.$container.addClass(CLASS_NAME_ERROR);
                field.$input
                    .addClass(CLASS_NAME_ERROR)
                    .attr({
                        'aria-describedby': field.errorId,
                        'aria-invalid': true
                    });
            }

            field.$input.on(EVENT_INPUT, () => this.onValidatedInput(field.$container, field.$input, field.errorId));
        });

        this.$form.find('[aria-invalid=true]').get(0)?.focus();
    }
}
